import "../../redesign.css";
import { Link } from "react-router-dom";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { domainUrl } from "app/Api/axios";
import { useLocation } from "react-router-dom";
import { solutionLinks } from "../../layout/RedesignNav";

const SolutionTemplate = ({
  eyebrow,
  title,
  titleGradient,
  subtitle,
  heroImg,
  heroImgAlt,
  chips = [],
  capabilities = [],
  flowSteps = [],
  benefits = [],
  relatedSlugs = [],
  metaTitle,
  metaDescription,
}) => {
  const location = useLocation();
  const canonicalURL = `${domainUrl}${location.pathname}`;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const related = solutionLinks.filter(s => relatedSlugs.includes(s.to));

  return (
    <div className="tt-page">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content="https://timetango.in/assets/img/hero/Hero_Combine.webp" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalURL} />
        <link rel="canonical" href={canonicalURL} />
        <meta property="og:site_name" content="TimeTango" />
        <meta name="robots" content="index, follow" />
        <meta name="google-site-verification" content="HeqJ8arMIZIDpCmRH1zlrNu601VS7Wp92JoTy_9bARM" />
      </Helmet>

      {/* ── HERO ── */}
      <section className="tt-hero" style={{paddingBottom:48}}>
        <div className="tt-wrap" style={{maxWidth:900}}>
          <div className="tt-eyebrow-pill">{eyebrow}</div>
          <h1 style={{fontSize:48,marginBottom:16}}>
            {title} <span className="tt-grad-text">{titleGradient}</span>
          </h1>
          <p className="tt-hero__sub">{subtitle}</p>
          <div className="tt-hero__ctas">
            <Link to="/company-register" className="tt-btn tt-btn--grad tt-btn--pill tt-btn--lg">Start free trial →</Link>
            <Link to="/pricing" className="tt-btn tt-btn--white tt-btn--pill tt-btn--lg">See pricing</Link>
          </div>
        </div>
        {heroImg && (
          <div className="tt-wrap" style={{maxWidth:960,position:"relative"}}>
            {chips.map((c, i) => (
              <div key={i} className={`tt-chip tt-chip--${(i % 4) + 1}`}>
                {c.dot && <span className="tt-chip__dot" />}
                {c.text}
              </div>
            ))}
            <div className="tt-hero__img-panel" style={{marginTop:32}}>
              <img src={heroImg} alt={heroImgAlt || title} style={{width:"100%",borderRadius:16}} />
            </div>
          </div>
        )}
      </section>

      {/* ── HOW IT WORKS (flow steps) ── */}
      {flowSteps.length > 0 && (
        <section className="tt-section" style={{textAlign:"center"}}>
          <div className="tt-wrap--grid">
            <span className="tt-eyebrow">How it works</span>
            <h2 style={{fontSize:36,marginBottom:40}}>Simple, powerful workflow</h2>
            <div className="tt-sol-steps">
              {flowSteps.map((step, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div className="tt-sol-step-arrow">→</div>}
                  <div className="tt-sol-step-card">
                    <div className="tt-sol-step-num">{i + 1}</div>
                    <h4>{step.title}</h4>
                    <p>{step.desc}</p>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CAPABILITIES GRID ── */}
      {capabilities.length > 0 && (
        <section className="tt-section tt-section--tinted" style={{textAlign:"center"}}>
          <div className="tt-wrap--grid">
            <span className="tt-eyebrow">Key capabilities</span>
            <h2 style={{fontSize:36,marginBottom:40}}>Everything you need</h2>
            <div className="tt-sol-caps">
              {capabilities.map((cap, i) => (
                <div key={i} className="tt-sol-cap-card">
                  <div className="tt-sol-cap-icon">{cap.icon}</div>
                  <h4>{cap.title}</h4>
                  <p>{cap.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── BENEFITS ── */}
      {benefits.length > 0 && (
        <section className="tt-section">
          <div className="tt-wrap--grid">
            <div style={{textAlign:"center",marginBottom:40}}>
              <span className="tt-eyebrow">Why TimeTango</span>
              <h2 style={{fontSize:36}}>Built for growing teams</h2>
            </div>
            <div className="tt-sol-benefits">
              {benefits.map((b, i) => (
                <div key={i} className="tt-sol-benefit-row">
                  <div className="tt-sol-benefit-check">✓</div>
                  <div>
                    <h4 style={{fontSize:17,fontWeight:600,color:"var(--tt-ink)",marginBottom:4}}>{b.title}</h4>
                    <p style={{fontSize:15,color:"var(--tt-body)",margin:0}}>{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── RELATED SOLUTIONS ── */}
      {related.length > 0 && (
        <section className="tt-section tt-section--tinted" style={{textAlign:"center"}}>
          <div className="tt-wrap--grid">
            <span className="tt-eyebrow">Related solutions</span>
            <h2 style={{fontSize:32,marginBottom:32}}>Works even better together</h2>
            <div className="tt-usecases" style={{gridTemplateColumns:`repeat(${Math.min(related.length, 3)}, 1fr)`}}>
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="tt-usecase-card" style={{textDecoration:"none"}}>
                  <h4 style={{color:"var(--tt-ink)"}}>{r.label}</h4>
                  <p>{r.desc}</p>
                  <span style={{fontSize:13,fontWeight:600,color:"var(--tt-magenta)"}}>Learn more →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="tt-section">
        <div className="tt-wrap--cta">
          <div className="tt-cta-band">
            <h2>Ready to get started?</h2>
            <p>Join 500+ companies using TimeTango. Free trial, no credit card required.</p>
            <div style={{display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap"}}>
              <Link to="/company-register" className="tt-btn tt-btn--white tt-btn--pill tt-btn--lg">Start free trial →</Link>
              <a href="https://calendly.com/amol-amlapure" target="_blank" rel="noreferrer" className="tt-btn tt-btn--pill tt-btn--lg" style={{background:"rgba(255,255,255,0.15)",color:"#fff",border:"1px solid rgba(255,255,255,0.3)"}}>Schedule a demo</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SolutionTemplate;
